import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import api from '../../../api'
import Responsive from '../../../assets/config/Responsive'
import Colors from '../../../assets/styles/Colors'
import { Container } from '../../../assets/styles/GlobalStyle'
import FraniBackgroundIcon from '../../../assets/v2/JSX/FraniBackgroundIcon'
import Loader from '../../../components/Loader'
import PageHeader from '../../../components/PageHeader'
import NavigationButton from '../../../components/v2/buttons/NavigationButton'
import HorizontalCard from '../../../components/v2/HorizontalCard'
import Title from '../../../components/v2/Title'
import { useAppSelector } from '../../../store'
import { IntTplState, setFilterField } from '../../../store/internationalTplSlice'
import { useDispatch } from 'react-redux'
import DatePicker from '../../../components/v2/DatePicker'
import { parseISO } from 'date-fns'
import dayjs from 'dayjs'
import { useInternationalTplPages } from './useInternationalTplPages'
import { useInternationalTplProgress } from './useInternationalTplProgress'
import { AppState } from '../../../store/appSlice'

const InternationalTplDays = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { lang } = useAppSelector((root: { app: AppState }) => root.app)
  const { filter } = useAppSelector((data: { internationalTpl: IntTplState }) => data.internationalTpl)

  // Use the hook to get pages and page data from Redux
  const { pages, pageData, isLoading: isPagesLoading } = useInternationalTplPages()
  const pageInfo = pageData['international-tpl-insurance-period']

  // Use progress hook to update passedCount and totalCount
  const { totalCount, passedCount, pages: progressPages } = useInternationalTplProgress()

  // Use pages from progress hook if available, otherwise from pages hook
  const displayPages = progressPages.length > 0 ? progressPages : pages

  const [getDays, { data: days, isLoading }] = api.useGetIntTplDaysMutation()

  const nextButtonRef = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (filter && filter.isoCode && !days && !isLoading) {
      const _getDaysRequest = async () => {
        try {
          await getDays({ countryCode: filter.isoCode }).unwrap()
        } catch (error: any) {
          if (error?.data?.errors?.constructor === Array) {
            const firstError = error.data.errors[0]
            const errorMessage = firstError.message
            toast.error(errorMessage)
          } else {
            toast.error('Მოხდა შეცდომა. Გთხოვთ სცადოთ მოგვიანებით... (_getDaysRequest) (single)')
          }
        }
      }
      // call request
      _getDaysRequest()
    }
    return () => {}
  }, [filter, getDays, days, isLoading])

  // Calculate date: current date + specified number of days
  const calculateDate = (daysToAdd: number): Date => {
    return dayjs().add(daysToAdd, 'day').toDate()
  }

  // auto redirect first page
  useEffect(() => {
    if (!filter?.isoCode) {
      navigate('/international-tpl-country')
    }
    return () => {}
  }, [filter?.isoCode, navigate])

  return isLoading || isPagesLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <PageHeader total={totalCount} passed={passedCount} pages={displayPages} />
      <Container>
        <Content>
          <Title text={pageInfo?.translations?.[lang]?.title} />
          <Block>
            {[...(days || [])]
              ?.sort((a, b) => a.days - b.days)
              ?.map((item, index) => (
                <HorizontalCard
                  className="horizontal-card"
                  contentStyle={{ alignItems: 'center' }}
                  key={index}
                  title={`${item?.days} ${t('day')}`}
                  imgCHildren={
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <FraniBackgroundIconStyle />
                      <div className="icon-days">{item?.days}</div>
                    </div>
                  }
                  selected={item?.days === filter?.day}
                  onClick={() => {
                    if (item && item.days && item.id) {
                      dispatch(
                        setFilterField({
                          fieldName: 'day',
                          fieldValue: item?.days,
                        }),
                      )

                      dispatch(
                        setFilterField({
                          fieldName: 'insurancePeriodId',
                          fieldValue: item?.id,
                        }),
                      )
                    }
                  }}
                >
                  {filter.day && filter.day === item.days ? (
                    <CalendarBox>
                      <hr />
                      <DatePicker
                        minDate={calculateDate(item.limitationOfStartDate || 0)}
                        maxDate={calculateDate(item.limitBeforeRealization || 0)}
                        className="multi-start-date-class"
                        label={`${t('startDate')}`}
                        dateFormat="dd MMMM yyyy"
                        open={true}
                        selectedDate={filter?.startDate ? parseISO(filter?.startDate as string) : null}
                        onchange={(value) => {
                          dispatch(
                            setFilterField({
                              fieldName: 'startDate',
                              fieldValue: dayjs(value).format('YYYY-MM-DD'),
                            }),
                          )

                          if (nextButtonRef.current) {
                            nextButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
                          }
                        }}
                      />
                    </CalendarBox>
                  ) : null}
                </HorizontalCard>
              ))}
          </Block>

          <Buttons>
            <NavigationButton
              text={`${t('back')}`}
              buttonType="prev"
              onClick={() => navigate('/international-tpl-country')}
            />
            <NavigationButton
              ref={nextButtonRef}
              text={`${t('next')}`}
              buttonType="next"
              disabled={filter.day && filter.startDate ? false : true}
              onClick={() => navigate('/international-tpl-personal-info')}
            />
          </Buttons>
        </Content>
      </Container>
    </Wrapper>
  )
}

const CalendarBox = styled.div`
  width: 100%;
  .react-datepicker-popper {
    position: unset !important;
    transform: unset !important;
    margin-top: 10px;
  }
  .react-datepicker {
    display: flex;
    justify-content: center;
  }
`

const FraniBackgroundIconStyle = styled(FraniBackgroundIcon)`
  height: 72px;

  ${Responsive.mobile} {
    height: 64px;
  }
`

const Content = styled.div`
  margin-top: 40px;
  margin-bottom: 32px;
  z-index: 2;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;

  ${Responsive.mobile} {
    margin-top: 24px;
  }
`
const Block = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  max-width: 432px;
  width: 100%;
  margin: 0 auto;

  .icon-days {
    position: absolute;
    top: 61px;
    font-size: 16px;
    color: ${Colors.primaryBlack};
    user-select: none;
  }

  ${Responsive.mobile} {
    margin-top: 24px;

    .icon-days {
      top: 62px;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
`
const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  background-color: ${Colors.backgroundGrey};
  padding-bottom: 20px;
  min-height: 100vh;

  .react-datepicker-wrapper {
    width: 100%;
  }
  .react-datepicker__input-container {
    user-select: none;
    pointer-events: none;
    cursor: default !important;
    input:focus {
      caret-color: transparent;
    }
  }
`

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-inline: auto;
  width: 100%;
  margin-top: 48px;
  max-width: 368px;

  button {
    width: 100%;
    padding-inline: 0px;
    &:first-child {
      margin-right: 20px;
    }
  }

  ${Responsive.mobile} {
    max-width: 296px;
    margin-top: 40px;
    button {
      &:first-child {
        margin-right: 15px;
      }
    }
  }
`

export default InternationalTplDays
